import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { CgEye, CgTrash } from "react-icons/cg";
import { useNavigate } from "react-router-dom";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import { buildInvoiceHtml, getInvoiceLogoDataUrl } from "../../utils/Inovoice";
import { formatDate } from "../../utils/date";

const formatOrderId = (order, index) => {
  const source = order.orderDisplayId || order.orderId || order.orderNumber;
  if (source && String(source).length <= 4) return String(source).padStart(4, "0");
  return String(1001 + index).padStart(4, "0");
};
const getCustomerName = (order) =>
  order.customerName || order.members?.[0]?.fullName || order.members?.fullName || "-";
const getLabStatus = (order) => order.labStatus || order.deliveryStatus || "Pending";
const statusClass = (value) => {
  if (value === "Completed") return "bg-success";
  if (value === "Processing") return "bg-info text-dark";
  if (value === "Pending") return "bg-warning text-dark";
  if (value === "Cancelled" || value === "Failed") return "bg-danger";
  return "bg-secondary";
};



const downloadInvoice = async (order) => {
  const printWindow = window.open("", "_blank", "width=900,height=1200");
  if (!printWindow) return;

  printWindow.document.write("<p style=\"font-family:Arial,sans-serif;padding:24px\">Preparing invoice…</p>");
  printWindow.document.close();

  const logoUrl = await getInvoiceLogoDataUrl();
  printWindow.document.open();
  printWindow.document.write(buildInvoiceHtml(order, logoUrl));
  printWindow.document.close();

  const printInvoice = () => {
    printWindow.focus();
    printWindow.onafterprint = () => printWindow.close();
    printWindow.print();
  };

  if (printWindow.document.readyState === "complete") {
    setTimeout(printInvoice, 250);
  } else {
    printWindow.onload = () => setTimeout(printInvoice, 250);
  }
};

const OrdersList = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [reportModal, setReportModal] = useState(false);
  const [reportOrderId, setReportOrderId] = useState("");
  const [reportFile, setReportFile] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteIds, setDeleteIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/orders?page=${pageNo}&limit=${limit}`, {
        method: "GET",
      });
      if (response?.data?.s === 200) {
        setOrders(response.data.r || []);
        setFilteredOrders(response.data.r || []);
        setTotalRecords(response.data.tr || 0);
      }
    } catch (error) {
      setSnack({ notification: { message: "Failed to load orders", type: "error" } });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, pageNo]);

  useEffect(() => {
    const search = query.trim().toLowerCase();
    if (!search) {
      setFilteredOrders(orders);
    } else {
      setFilteredOrders(
        orders.filter((order, index) =>
          [
            formatOrderId(order, (pageNo - 1) * limit + index),
            order.orderNumber,
            getCustomerName(order),
            order.paymentStatus,
            order.orderStatus,
            getLabStatus(order),
          ]
            .join(" ")
            .toLowerCase()
            .includes(search)
        )
      );
    }
  }, [query, orders, pageNo, limit]);

  const totalPages = Math.ceil((totalRecords || filteredOrders.length) / limit) || 1;
  const orderOffset = (pageNo - 1) * limit;
  const visibleIds = filteredOrders.map((order) => order._id).filter(Boolean);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const toggleVisibleSelection = () => {
    setSelectedIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds]))
    );
  };

  const openDeleteModal = (ids) => {
    if (!ids.length) {
      setSnack({ notification: { message: "Select at least one order", type: "error" } });
      return;
    }
    setDeleteIds(ids);
    setDeleteModal(true);
  };

  const deleteOrders = async () => {
    try {
      setLoading(true);
      const response = await AuthInterceptor("/admin/orders", {
        method: "DELETE",
        body: JSON.stringify({ ids: deleteIds }),
      });
      if (response?.data?.s === 200 || response?.status === 200) {
        setSnack({
          notification: {
            message: response?.data?.m || "Orders deleted successfully",
            type: "success",
          },
        });
        setSelectedIds((prev) => prev.filter((id) => !deleteIds.includes(id)));
        setDeleteModal(false);
        fetchOrders();
      } else {
        setSnack({
          notification: { message: response?.data?.m || "Error deleting orders", type: "error" },
        });
      }
    } catch (error) {
      setSnack({ notification: { message: "Error deleting orders", type: "error" } });
    } finally {
      setLoading(false);
      setDeleteIds([]);
    }
  };

  const openReportUpload = (orderId) => {
    setReportOrderId(orderId);
    setReportFile(null);
    setReportModal(true);
  };

  const uploadReport = async (event) => {
    event.preventDefault();
    if (!reportOrderId || !reportFile) {
      setSnack({ notification: { message: "Order and report file are required", type: "error" } });
      return;
    }
    const formData = new FormData();
    formData.append("orderId", reportOrderId);
    formData.append("report", reportFile);
    try {
      setLoading(true);
      const response = await AuthInterceptor("/admin/order/report", {
        method: "POST",
        body: formData,
      });
      if ([200, 201].includes(response?.data?.s) || [200, 201].includes(response?.status)) {
        setSnack({ notification: { message: "Report uploaded successfully", type: "success" } });
        setReportModal(false);
        fetchOrders();
      } else {
        setSnack({
          notification: { message: response?.data?.m || "Error uploading report", type: "error" },
        });
      }
    } catch (error) {
      setSnack({ notification: { message: error?.response?.data?.m || error?.message || "Error uploading report", type: "error" } });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && <Toaster notification={snack.notification} onClose={() => setSnack(null)} />}

      <div className="card shadow-sm border-0 rounded-3 p-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <h4 className="mb-0">Orders</h4>
          <div className="d-flex flex-wrap gap-2">
            <input
              className="form-control"
              placeholder="Search orders..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ minWidth: "260px" }}
            />
            <select
              className="form-select w-auto"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPageNo(1);
              }}
            >
              {[5, 10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <span className="text-muted small">{selectedIds.length} selected</span>
          <button className="btn btn-sm btn-outline-danger" onClick={() => openDeleteModal(selectedIds)}>
            Delete
          </button>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ width: "48px" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleSelection}
                  />
                </th>
                <th>Order Number</th>
                <th>Order Date</th>
                <th>Customer Name</th>
                <th>Payment Status</th>
                <th>Order Status</th>
                <th>Lab Status</th>
                <th>Report Upload</th>
                <th>Download Invoice</th>
                <th style={{ width: "130px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length > 0 ? (
                filteredOrders.map((order, index) => (
                  <tr key={order._id}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedIds.includes(order._id)}
                        onChange={() => toggleRowSelection(order._id)}
                      />
                    </td>
                    <td>{order.orderNumber || formatOrderId(order, orderOffset + index)}</td>
                    <td>{formatDate(order.createdAt)}</td>
                    <td>{getCustomerName(order)}</td>
                    <td>
                      <span className={`badge ${statusClass(order.paymentStatus)}`}>
                        {order.paymentStatus || "Pending"}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${statusClass(order.orderStatus)}`}>
                        {order.orderStatus || "Pending"}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${statusClass(getLabStatus(order))}`}>
                        {getLabStatus(order)}
                      </span>
                    </td>
                    <td>
                      {order.report?.reportUrl ? (
                        <a href={order.report.reportUrl} target="_blank" rel="noopener noreferrer">
                          View
                        </a>
                      ) : (
                        <button
                          className="btn btn-sm btn-outline-custom"
                          onClick={() => openReportUpload(order._id)}
                        >
                          Upload
                        </button>
                      )}
                    </td>
                    <td>
                      <button className="btn btn-sm btn-outline-custom" onClick={() => downloadInvoice(order)}>
                        Save as PDF
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => navigate(`/orders/view/${order._id}`)}
                      >
                        <CgEye size={16} />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger ms-2"
                        onClick={() => openDeleteModal([order._id])}
                      >
                        <CgTrash size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="text-center py-4 text-muted">
                    No orders found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-3">
          <button
            className="btn btn-outline-custom btn-sm"
            disabled={pageNo === 1}
            onClick={() => setPageNo((page) => page - 1)}
          >
            Prev
          </button>
          <span>
            Page <strong>{pageNo}</strong> of <strong>{totalPages}</strong>
          </span>
          <button
            className="btn btn-outline-custom btn-sm"
            disabled={pageNo >= totalPages}
            onClick={() => setPageNo((page) => page + 1)}
          >
            Next
          </button>
        </div>
      </div>

      <Modal show={reportModal} onHide={() => setReportModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Upload Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={uploadReport}>
            <div className="mb-3">
              <label className="form-label">Report</label>
              <input
                className="form-control"
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(e) => setReportFile(e.target.files?.[0] || null)}
              />
              <div className="text-muted small mt-1">PDF, DOC, or DOCX</div>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setReportModal(false)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-outline-custom">
                Upload
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <Modal show={deleteModal} onHide={() => setDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Orders</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This action can not be reversed. Are you sure you want to delete{" "}
            <strong>{deleteIds.length}</strong> order{deleteIds.length > 1 ? "s" : ""}?
          </p>
          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-outline-secondary" onClick={() => setDeleteModal(false)}>
              Cancel
            </button>
            <button type="button" className="btn btn-danger" onClick={deleteOrders}>
              Delete
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default OrdersList;
